import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, Package, ShoppingCart, Users, FileText, X, ArrowRight, Loader2, Command } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GlobalSearch = ({ user, isOpen, onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({
    products: [],
    orders: [],
    suppliers: [],
    documents: []
  });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setResults({ products: [], orders: [], suppliers: [], documents: [] });
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      const allResults = [
        ...results.products.map(p => ({ ...p, type: 'product' })),
        ...results.orders.map(o => ({ ...o, type: 'order' })),
        ...results.suppliers.map(s => ({ ...s, type: 'supplier' })),
        ...results.documents.map(d => ({ ...d, type: 'document' }))
      ];

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && allResults[selectedIndex]) {
        e.preventDefault();
        handleSelect(allResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  // Search function with debounce
  const searchAll = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ products: [], orders: [], suppliers: [], documents: [] });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/global-search?q=${encodeURIComponent(searchQuery)}&limit=5`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setResults(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchAll(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchAll]);

  const handleSelect = (item) => {
    onNavigate(item.type, item);
    onClose();
  };

  const getIcon = (type) => {
    switch (type) {
      case 'product': return <Package className="w-4 h-4 text-blue-500" />;
      case 'order': return <ShoppingCart className="w-4 h-4 text-green-500" />;
      case 'supplier': return <Users className="w-4 h-4 text-purple-500" />;
      case 'document': return <FileText className="w-4 h-4 text-orange-500" />;
      default: return <Search className="w-4 h-4 text-slate-400" />;
    }
  };

  const allResults = [
    ...results.products.map(p => ({ ...p, type: 'product' })),
    ...results.orders.map(o => ({ ...o, type: 'order' })),
    ...results.suppliers.map(s => ({ ...s, type: 'supplier' })),
    ...results.documents.map(d => ({ ...d, type: 'document' }))
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-[15vh] z-[100] p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search products, orders, suppliers, documents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-lg outline-none placeholder:text-slate-400"
          />
          {loading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-500 font-mono">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto" ref={resultsRef}>
          {query.length < 2 ? (
            <div className="p-8 text-center text-slate-500">
              <Command className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Quick Search</p>
              <p className="text-sm mt-1">Type at least 2 characters to search</p>
              <div className="flex justify-center gap-6 mt-4 text-xs">
                <span className="flex items-center gap-1"><Package className="w-3 h-3" /> Products</span>
                <span className="flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> Orders</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Suppliers</span>
                <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> Documents</span>
              </div>
            </div>
          ) : allResults.length === 0 && !loading ? (
            <div className="p-8 text-center text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No results found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="py-2">
              {/* Products Section */}
              {results.products.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                    Products ({results.products.length})
                  </div>
                  {results.products.map((item, idx) => {
                    const globalIdx = idx;
                    return (
                      <button
                        key={`product-${item.id || idx}`}
                        onClick={() => handleSelect({ ...item, type: 'product' })}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left
                          ${selectedIndex === globalIdx ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                      >
                        {getIcon('product')}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{item.product_name}</p>
                          <p className="text-xs text-slate-500">
                            {item.packing} • Qty: {item.available_qty_srh || 0} (SRH), {item.available_qty_winlife || 0} (WL)
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Orders Section */}
              {results.orders.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                    Orders ({results.orders.length})
                  </div>
                  {results.orders.map((item, idx) => {
                    const globalIdx = results.products.length + idx;
                    return (
                      <button
                        key={`order-${item.id || idx}`}
                        onClick={() => handleSelect({ ...item, type: 'order' })}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 transition-colors text-left
                          ${selectedIndex === globalIdx ? 'bg-green-50 border-l-2 border-green-500' : ''}`}
                      >
                        {getIcon('order')}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{item.product_name}</p>
                          <p className="text-xs text-slate-500">
                            To: {item.order_given_to} • Qty: {item.order_qty} • {item.order_given_date}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                          {item.status || 'pending'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Suppliers Section */}
              {results.suppliers.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                    Suppliers ({results.suppliers.length})
                  </div>
                  {results.suppliers.map((item, idx) => {
                    const globalIdx = results.products.length + results.orders.length + idx;
                    return (
                      <button
                        key={`supplier-${item.name || idx}`}
                        onClick={() => handleSelect({ ...item, type: 'supplier' })}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left
                          ${selectedIndex === globalIdx ? 'bg-purple-50 border-l-2 border-purple-500' : ''}`}
                      >
                        {getIcon('supplier')}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{item.name}</p>
                          <p className="text-xs text-slate-500">
                            Balance: ₹{(item.balance || 0).toLocaleString()}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Documents Section */}
              {results.documents.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                    Documents ({results.documents.length})
                  </div>
                  {results.documents.map((item, idx) => {
                    const globalIdx = results.products.length + results.orders.length + results.suppliers.length + idx;
                    return (
                      <button
                        key={`doc-${item.id || idx}`}
                        onClick={() => handleSelect({ ...item, type: 'document' })}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors text-left
                          ${selectedIndex === globalIdx ? 'bg-orange-50 border-l-2 border-orange-500' : ''}`}
                      >
                        {getIcon('document')}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{item.original_name}</p>
                          <p className="text-xs text-slate-500">
                            {item.category} • {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">Enter</kbd>
              Select
            </span>
          </div>
          <span>
            Press <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">⌘K</kbd> anytime to search
          </span>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
